import type { ExcelOperationClaimRefusal } from "./userSkills/operationClaim";

/**
 * The contract between a submission and whatever offered it.
 *
 * A submission is "accepted" once the request is owned: its operation is
 * frozen, its run is registered, and its loading state and user message are
 * committed. Everything before that point can still refuse, and a refusal must
 * leave the composer exactly as the user left it — same prompt, same Skill
 * chips, same continuation button — because nothing was started.
 *
 * Acceptance deliberately does not wait for the run. The pipeline behind an
 * accepted request takes minutes; the affordances that reset on submit have to
 * settle in the same interaction.
 */

/** Why a submission never started. */
export type ExcelSubmissionRefusal =
  | "no_active_chat"
  | "workbook_changed_during_submit"
  | "account_changed_during_submit"
  | "start_failed"
  | ExcelOperationClaimRefusal;

export interface ExcelSubmissionResult {
  readonly accepted: boolean;
  readonly reason?: ExcelSubmissionRefusal;
  /**
   * Whether sending the same submission again could succeed. A workbook or
   * chat mismatch is navigation the user can undo; a lost Skill binding or an
   * account change is not, and the affordance behind it should go away.
   */
  readonly retryable: boolean;
}

export interface ExcelSubmissionOutcome extends ExcelSubmissionResult {
  /** Settles when the run ends. Already settled when the request was refused. */
  readonly completed: Promise<void>;
}

export interface ExcelSubmissionHandoff {
  accept(): void;
  refuse(reason: ExcelSubmissionRefusal, options?: { retryable?: boolean }): void;
}

export interface ExcelSubmissionHandoffController {
  readonly handoff: ExcelSubmissionHandoff;
  readonly result: Promise<ExcelSubmissionResult>;
  /**
   * Settles a still-pending handoff. The run calls this from its `finally` so
   * a throw between the first statement and acceptance cannot leave the caller
   * waiting on a promise nothing will resolve.
   */
  settle(): void;
}

const ACCEPTED: ExcelSubmissionResult = Object.freeze({ accepted: true, retryable: false });

export function createExcelSubmissionHandoff(): ExcelSubmissionHandoffController {
  let publishResult!: (result: ExcelSubmissionResult) => void;
  const result = new Promise<ExcelSubmissionResult>((resolve) => {
    publishResult = resolve;
  });
  let settled = false;
  const publish = (next: ExcelSubmissionResult) => {
    // First settlement wins: a refusal followed by a stray accept (or the
    // `finally` safety net firing after a real outcome) must not rewrite what
    // the caller already acted on.
    if (settled) return;
    settled = true;
    publishResult(next);
  };

  return {
    handoff: {
      accept: () => publish(ACCEPTED),
      refuse: (reason, options) =>
        publish({ accepted: false, reason, retryable: options?.retryable ?? true }),
    },
    result,
    settle: () => publish({ accepted: false, reason: "start_failed", retryable: true }),
  };
}
